/**
 * 案件の一覧（開発）の表示の決まり。API の応答を、画面に出す区分・札の Backlog の欄・帯の文言に直す。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の応答から、出す区分（置き場所は0件なら出さない・見るだけの区分は0件なら出さない）、札の Backlog の欄、形の問題の説明、スプシを読んだ時刻の書き方を決める
 * @graph-connects none
 */
import type {
  ProductBacklogCard,
  ProductBacklogLayoutIssue,
  ProductBacklogSection,
} from '@cortex/domain-ac-mens-hub-api';

/**
 * 要件定義アサイン希望で「次のアサイン候補」として区切る件数（上から数える）。
 * @graph-connects none
 */
export const NEXT_ASSIGNMENT_CANDIDATES = 5;

/**
 * 画面に出す区分。arrange は並べる区分（Hub の並び順のまま）、viewOnly は区切り線の下で畳む見るだけの区分。
 * @graph-connects none
 */
export interface VisibleSections {
  arrange: ProductBacklogSection[];
  viewOnly: ProductBacklogSection[];
}

/**
 * 出す区分を決める。置き場所が決まっていない案件と見るだけの区分は0件なら出さず、並べる区分は0件でも出す。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の区分を、並べる区分（0件でも出す。置き場所だけは0件なら出さない）と、0件なら出さない見るだけの区分に分ける
 * @graph-connects none
 */
export function visibleSections(sections: readonly ProductBacklogSection[]): VisibleSections {
  return {
    arrange: sections.filter(
      (s) => s.arrangeable && (s.kind !== 'unassigned' || s.cards.length > 0),
    ),
    viewOnly: sections.filter((s) => !s.arrangeable && s.cards.length > 0),
  };
}

/**
 * 札の Backlog の欄。
 * - values: 状態と PL（値が無い項目は null）
 * - notFound: Backlog の値は読めたが、このキーは見つからなかった
 * - unavailable: Backlog の値を読めなかった
 * - noKey: スプシの行にキーが無い
 * @graph-connects none
 */
export type BacklogCell =
  | { kind: 'values'; status: string | null; pl: string | null }
  | { kind: 'notFound' }
  | { kind: 'unavailable' }
  | { kind: 'noKey' };

/**
 * 札の Backlog の欄を決める。値が無い札は、一覧の読み取りの状態で「見つからない」と「読めない」を分ける。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件の札に出す Backlog の状態と PL を決め、値が無いときは Backlog に見つからないのか読めなかったのかを分ける
 * @graph-connects none
 */
export function backlogCellOf(
  card: ProductBacklogCard,
  backlogStatus: 'ok' | 'unavailable',
): BacklogCell {
  if (card.key === null) return { kind: 'noKey' };
  if (card.backlog !== null) {
    return { kind: 'values', status: card.backlog.status, pl: card.backlog.pl };
  }
  return backlogStatus === 'ok' ? { kind: 'notFound' } : { kind: 'unavailable' };
}

/**
 * 文言を引く関数（react-i18next の t と同じ形）。
 * @graph-connects none
 */
export type Translate = (key: string, params?: Record<string, string>) => string;

/**
 * 形の問題を、帯に出す説明の文にする。行番号が複数あるときは最初の行と残りの件数を出す。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business スプシの形の問題（見出しの欠け・知らない区切り・区分の重複と欠け・どこにも属さない行など）を、帯に出す説明の文にする
 * @graph-connects none
 */
export function describeLayoutIssue(issue: ProductBacklogLayoutIssue, t: Translate): string {
  switch (issue.type) {
    case 'missing_header':
      return t('productBacklog.layoutIssue.missingHeader', { header: issue.header });
    case 'unknown_separator':
      return t('productBacklog.layoutIssue.unknownSeparator', {
        label: issue.label,
        row: String(issue.rowNumber),
      });
    case 'ambiguous_separator':
      return t('productBacklog.layoutIssue.ambiguousSeparator', {
        label: issue.label,
        row: String(issue.rowNumber),
      });
    case 'duplicate_section':
      return t('productBacklog.layoutIssue.duplicateSection', {
        section: t(`productBacklog.sections.${issue.kind}.name`),
      });
    case 'missing_section':
      return t('productBacklog.layoutIssue.missingSection', {
        section: t(`productBacklog.sections.${issue.kind}.name`),
      });
    case 'unclaimed_rows':
      return t('productBacklog.layoutIssue.unclaimedRows', {
        row: String(issue.rowNumbers[0]),
        more: String(issue.rowNumbers.length - 1),
      });
    case 'contested_rows':
      return t('productBacklog.layoutIssue.contestedRows', {
        row: String(issue.rowNumbers[0]),
        more: String(issue.rowNumbers.length - 1),
      });
    default: {
      const unreachable: never = issue;
      return unreachable;
    }
  }
}

/**
 * 日本時間（UTC+9。夏時間は無い）。
 * @graph-connects none
 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

/**
 * 時刻を日本時間の「YYYY-MM-DDTHH:MM」にする。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 時刻を日本時間の日付と時:分の文字列にする
 * @graph-connects none
 */
function jstMinute(date: Date): string {
  return new Date(date.getTime() + JST_OFFSET_MS).toISOString().substring(0, 16);
}

/**
 * スプシを読んだ時刻の書き方（日本時間）。今日なら「15:32」、それ以前なら「10/07 15:32」。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business スプシを読んだ時刻を、今日なら時:分、別の日なら月/日 時:分で日本時間で書く
 * @graph-connects jstMinute [calls] 日本時間の日付と時刻
 */
export function formatReadAt(iso: string, now: Date): string {
  const read = jstMinute(new Date(iso));
  const time = read.substring(11, 16);
  return read.substring(0, 10) === jstMinute(now).substring(0, 10)
    ? time
    : `${read.substring(5, 7)}/${read.substring(8, 10)} ${time}`;
}
